import { create } from 'zustand';
import {
  featureFlagDefaults,
  type AnalyticsContext,
  type FeatureFlagKey,
} from '@galapagos/product-analytics';
import {
  ensureAuthTeardownParticipant,
  type AuthTeardownParticipant,
} from '@/services/auth-teardown';

/** The server-evaluated flags exactly as `GET /api/v1/analytics/context` returns them. */
export type FeatureFlagSnapshot = AnalyticsContext['flags'];

export type FeatureFlagValue = boolean | string;

/**
 * The server-authoritative feature-flag snapshot for the signed-in member, the
 * web counterpart of the mobile flag store. The client never evaluates a flag
 * or picks a variant: it holds what the server sent, and until a snapshot lands
 * (signed-out visitors, a failed context read, after sign-out) every read falls
 * back to the repo-declared default — so a flag that is off by default can
 * never flicker on from a network hiccup.
 *
 * In memory only: a reload starts from defaults again.
 */
export interface FeatureFlagState {
  snapshot: FeatureFlagSnapshot | null;
  setSnapshot: (snapshot: FeatureFlagSnapshot) => void;
  clearSnapshot: () => void;
}

export const useFeatureFlagStore = create<FeatureFlagState>((set) => ({
  snapshot: null,
  setSnapshot: (snapshot) => set({ snapshot }),
  clearSnapshot: () => set({ snapshot: null }),
}));

// The registry is static for the life of the bundle, so the defaults map is built once.
const declaredDefaults = featureFlagDefaults();

function resolveFlagValue(
  snapshot: FeatureFlagSnapshot | null,
  key: FeatureFlagKey,
): FeatureFlagValue {
  const serverValue = snapshot?.[key];
  if (serverValue !== undefined) return serverValue;
  return declaredDefaults[key] ?? false;
}

function asEnabled(value: FeatureFlagValue): boolean {
  if (typeof value === 'boolean') return value;
  return value !== 'control';
}

/**
 * Read one flag's value: the server's, else the declared default. Use this to
 * vary a screen by variant; use `useFeatureFlagEnabled` to gate one.
 */
export function useFeatureFlagValue(key: FeatureFlagKey): FeatureFlagValue {
  // A primitive selector: Zustand compares it by value, so the reader re-renders
  // only when this flag's resolved value changes, not on every snapshot swap.
  return useFeatureFlagStore((state) => resolveFlagValue(state.snapshot, key));
}

/**
 * Read one flag as a boolean gate. A variant counts as enabled for anything
 * other than `control`, mirroring the PostHog boolean-or-variant flag shape;
 * an unknown key resolves to false.
 */
export function useFeatureFlagEnabled(key: FeatureFlagKey): boolean {
  return useFeatureFlagStore((state) => asEnabled(resolveFlagValue(state.snapshot, key)));
}

/** Non-hook read for imperative call sites (e.g. navigation handlers). */
export function getFeatureFlagValue(key: FeatureFlagKey): FeatureFlagValue {
  return resolveFlagValue(useFeatureFlagStore.getState().snapshot, key);
}

/** Non-hook gate for imperative call sites. */
export function isFeatureFlagEnabled(key: FeatureFlagKey): boolean {
  return asEnabled(getFeatureFlagValue(key));
}

// Bumped by every direct write, every clear and every new pending write, so a
// context read that started before any of them can never land after it — a
// slow response from an ended session must not restore its flags.
let snapshotGeneration = 0;

/** A claim on the next snapshot write, taken before an asynchronous read starts. */
export interface FeatureFlagSnapshotWrite {
  /** False once the session ended or a newer write took over; stop work for it. */
  readonly isCurrent: () => boolean;
  /** Store the snapshot if this claim is still current; reports whether it landed. */
  readonly commit: (snapshot: FeatureFlagSnapshot) => boolean;
}

export function beginFeatureFlagSnapshotWrite(): FeatureFlagSnapshotWrite {
  snapshotGeneration += 1;
  const generation = snapshotGeneration;
  const isCurrent = () => generation === snapshotGeneration;
  return {
    isCurrent,
    commit: (snapshot) => {
      if (!isCurrent()) return false;
      useFeatureFlagStore.getState().setSnapshot(snapshot);
      return true;
    },
  };
}

/**
 * Write a snapshot unconditionally. It wins over any read still in flight,
 * which is then discarded — so a sign-in still loading its flags loses to it.
 * Asynchronous reads should go through `beginFeatureFlagSnapshotWrite` instead.
 */
export function setFeatureFlagSnapshot(snapshot: FeatureFlagSnapshot): void {
  snapshotGeneration += 1;
  useFeatureFlagStore.getState().setSnapshot(snapshot);
}

export function clearFeatureFlagSnapshot(): void {
  snapshotGeneration += 1;
  useFeatureFlagStore.getState().clearSnapshot();
}

/**
 * Flag values were evaluated for one member, so they must not outlive the
 * session: a second member in the same tab starts from declared defaults.
 */
export const featureFlagTeardownParticipant: AuthTeardownParticipant = {
  id: 'feature-flags',
  clear: () => clearFeatureFlagSnapshot(),
};

// Joins teardown at module load, the moment the store can hold member data.
ensureAuthTeardownParticipant(featureFlagTeardownParticipant);
